import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import LoginPage from './components/LoginPage';
import LandingPage from './components/LandingPage';
import AppLayout from './App';
import ChatInterface from './components/ChatInterface';
import RealtimeConsultPage from './components/RealtimeConsultPage';
import DietaryRecipes from './components/DietaryRecipes';
import DietaryRecipeDetail from './components/DietaryRecipeDetail';
import GeneratedRecipePreview from './components/GeneratedRecipePreview';
import HerbLibrary from './components/HerbLibrary';
import FaceDiagnosisPage from './components/FaceDiagnosisPage';
import ProductInfo from './components/ProductInfo';
import UserProfile from './components/UserProfile';
import PrescriptionDetail from './components/PrescriptionDetail';
import MedicalRecordDetailPage from './components/MedicalRecordDetailPage';
import ImageReportDetailPage from './components/ImageReportDetailPage';
import HealthCalendarPage from './components/HealthCalendarPage';
import HealthCalendarDetailPage from './components/HealthCalendarDetailPage';
import CommunityLandingPage from './components/CommunityLandingPage';
import CommunityApp from './community/CommunityApp';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { hydrateFromStorage } from './redux/authSlice';

const RequireAuth: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const location = useLocation();

  if (!accessToken) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
};

const pageVariants = {
  initial: { opacity: 0, y: 36, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
};

const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    layout
    variants={pageVariants}
    initial="initial"
    animate="animate"
    transition={{ type: 'spring', stiffness: 85, damping: 26, mass: 1.05 }}
    style={{ willChange: 'transform, opacity' }}
  >
    {children}
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;

    if (currentPath !== previousPath) {
      console.debug('[Router] route change detected', { previousPath, currentPath });

      if (currentPath === '/app/user-profile' && previousPath !== '/app/user-profile') {
        console.debug('[Router] entering user profile route');
      }

      if (previousPath === '/app/user-profile' && currentPath !== '/app/user-profile') {
        console.debug('[Router] leaving user profile route toward', currentPath);
      }

      previousPathRef.current = currentPath;
    }
  }, [location.pathname]);

  useEffect(() => {
    dispatch(hydrateFromStorage());
  }, [dispatch]);

  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/" element={<PageTransition><LandingPage /></PageTransition>} />
      <Route path="/login" element={<PageTransition><LoginPage /></PageTransition>} />
      <Route path="/app" element={<AppLayout />}>
        <Route index element={<Navigate to="/app/chat" replace />} />
        <Route
          path="chat"
          element={(
            <PageTransition>
              <RequireAuth>
                <ChatInterface />
              </RequireAuth>
            </PageTransition>
          )}
        />
        <Route
          path="realtime-consult"
          element={(
            <PageTransition>
              <RequireAuth>
                <RealtimeConsultPage />
              </RequireAuth>
            </PageTransition>
          )}
        />
        <Route path="dietary-recipes" element={<PageTransition><DietaryRecipes /></PageTransition>} />
        <Route
          path="dietary-recipes/generated"
          element={<PageTransition><GeneratedRecipePreview /></PageTransition>}
        />
        <Route
          path="dietary-recipes/:id"
          element={<PageTransition><DietaryRecipeDetail /></PageTransition>}
        />
        <Route path="herb-library" element={<PageTransition><HerbLibrary /></PageTransition>} />
        <Route path="face-diagnosis" element={<PageTransition><FaceDiagnosisPage /></PageTransition>} />
        <Route path="product-info" element={<PageTransition><ProductInfo /></PageTransition>} />
        <Route
          path="user-profile"
          element={(
            <PageTransition>
              <RequireAuth>
                <UserProfile />
              </RequireAuth>
            </PageTransition>
          )}
        />
        <Route
          path="prescriptions/:id"
          element={(
            <PageTransition>
              <RequireAuth>
                <PrescriptionDetail />
              </RequireAuth>
            </PageTransition>
          )}
        />
        <Route
          path="medical-records/:id"
          element={(
            <PageTransition>
              <RequireAuth>
                <MedicalRecordDetailPage />
              </RequireAuth>
            </PageTransition>
          )}
        />
        <Route
          path="image-reports/:reportId"
          element={(
            <PageTransition>
              <RequireAuth>
                <ImageReportDetailPage />
              </RequireAuth>
            </PageTransition>
          )}
        />
        <Route
          path="community-landing"
          element={(
            <PageTransition>
              <RequireAuth>
                <CommunityLandingPage />
              </RequireAuth>
            </PageTransition>
          )}
        />
        <Route
          path="health-calendar"
          element={(
            <PageTransition>
              <RequireAuth>
                <HealthCalendarPage />
              </RequireAuth>
            </PageTransition>
          )}
        />
        <Route
          path="health-calendar/detail/:calendarId"
          element={(
            <PageTransition>
              <RequireAuth>
                <HealthCalendarDetailPage />
              </RequireAuth>
            </PageTransition>
          )}
        />
      </Route>
      <Route
        path="/app/community/*"
        element={(
          <RequireAuth>
            <CommunityApp />
          </RequireAuth>
        )}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const AppRouter = () => {
  return (
    <Router>
      <AnimatedRoutes />
    </Router>
  );
};

export default AppRouter;